import type { Game } from "@rally/rating";
import type { AuctionCurrency } from "./currency.js";

export type Sport = "badminton" | "pickleball" | "tabletennis";
export type Kind = "singles" | "doubles";
export type MemberStatus = "pending" | "active" | "suspended" | "rejected";
export type MemberRole = "player" | "organiser" | "admin";
export type MatchStatus = "pending" | "confirmed" | "disputed" | "void";
export type SeedLevel = "new" | "casual" | "club" | "competitive";

/**
 * Ranking category. It splits the leaderboard and never the rating: a player has one
 * rating per discipline, and mixed is not a third one. A mixed match is recorded and does
 * not move any of them — see `matchRates` below.
 */
export type MemberCategory = "men" | "women";

/**
 * What kind of match it was. Derived by the database from who played — never chosen, so it
 * can never contradict the court. The picker on the log screen filters candidates; it does
 * not label the match.
 *
 * Null when any participant has no category: a match with an unknown player is unknowable,
 * and guessing from the rest would be inventing the answer.
 */
export type MatchCategory = "mens" | "womens" | "mixed";

export const MATCH_CATEGORY_LABEL: Record<MatchCategory, string> = {
  mens: "Men's",
  womens: "Women's",
  mixed: "Mixed",
};

/**
 * Whether a casual match of this category moves ratings — the mixed half of `moves_ratings`
 * in SQL, which is the authority. It exists so the log screen can warn you before you play
 * rather than after, when there is no match row yet to ask. A match that exists carries
 * `movesRatings` from the database; read that instead, because it also knows about events
 * that do not count.
 *
 * Null rates. Null means a participant had no category set when the match was logged, not
 * that it was mixed, and taking a rating off four people over one empty profile field is
 * the wrong way to be strict.
 */
export const matchRates = (category: MatchCategory | null): boolean => category !== "mixed";

/** What the database will derive for this set of people. Mirrors match_category_of. */
export function deriveMatchCategory(categories: (MemberCategory | null)[]): MatchCategory | null {
  if (categories.length === 0 || categories.some((c) => c === null)) return null;
  if (!categories.includes("women")) return "mens";
  if (!categories.includes("men")) return "womens";
  return "mixed";
}

export interface Member {
  id: string;
  displayName: string;
  handle: string | null;
  avatarUrl: string | null;
  status: MemberStatus;
  role: MemberRole;
  selfAssessed: SeedLevel | null;
  category: MemberCategory | null;
  sports: Sport[];
  dominantHand: "left" | "right" | null;
  preferredSide: "left" | "right" | null;
  createdAt: string;
  /**
   * No login of their own — in practice a child, looked after by guardians who act for them
   * in registration. Never in a casual match; the database refuses it. See
   * 20260929000100.
   */
  managed: boolean;
  /**
   * Plays as a junior: enters boys' and girls' events, not men's, women's or senior ones.
   * Stated, never derived — no age is stored. Changed only by an admin once the member
   * exists. See 20260930000100 and 20260930000200.
   */
  junior: boolean;
  /**
   * Plays as a senior: enters senior men's and senior women's events, and adult ones. Never
   * also a junior; neither means adult. Stated like `junior`, and changed only by an admin.
   * See 20261001000200.
   */
  senior: boolean;
}

export interface MatchSummary {
  id: string;
  sport: Sport;
  kind: Kind;
  category: MatchCategory | null;
  status: MatchStatus;
  games: Game[];
  winner: "A" | "B";
  playedAt: string;
  enteredBy: string;
  /** Casual, or part of an event. */
  context: MatchContext;
  /**
   * Whether this match can move ratings — the database's own `moves_ratings`, read as a
   * computed column rather than re-derived here. False for mixed, and for a match in an event
   * that does not count towards ratings. See 20260929000200.
   */
  movesRatings: boolean;
  /**
   * The event it was played at, once there is one.
   *
   * A tournament match is an ordinary match everywhere — the log, your record, the
   * confirmation queue, the rating engine — because `score_fixture` writes into the same
   * table. This is the one thing that would otherwise be lost on the way to the screen: that
   * it happened at Tuesday's tie rather than on a Tuesday.
   */
  event: { id: string; name: string } | null;
  /** Every participant, both sides, in the order the database returned them. */
  participants: MatchParticipant[];
}

export type MatchContext = "casual" | "event";

export interface MatchParticipant {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  side: "A" | "B";
  confirmedAt: string | null;
}

/**
 * The catalogue of sports the schema knows about. A deployment serves exactly one of
 * them — see sport.ts — so this is a lookup table, not a menu.
 */
/**
 * How many games a match is, and what each is played to.
 *
 * Community play is not a tournament: three to 21 most nights, one to 30 when courts are
 * tight, five to 11 when someone fancies it. `bestOf` decides when the match is clinched.
 * `target` is a label — nothing validates against it, because a game that ended 24-22 or
 * was stopped at 15-9 is still what happened.
 *
 * The rating engine does not care. `outcomeScore` works on the *share* of games and points
 * won, never on an absolute target, so a single game to 30 rates correctly with no change
 * to the maths. The one consequence worth knowing: winning one game counts as a whole
 * game-share, the same as a 2-0 sweep, so a single game moves a rating as much as a
 * straight-sets win. Fewer points played is not currently treated as weaker evidence.
 */
export interface MatchFormat {
  bestOf: number;
  target: number;
}

export const formatLabel = (format: MatchFormat) =>
  format.bestOf === 1
    ? `1 game to ${format.target}`
    : `Best of ${format.bestOf} to ${format.target}`;

export const SPORTS: {
  value: Sport;
  label: string;
  appName: string;
  shortName: string;
  target: number;
  formats: MatchFormat[];
}[] = [
  {
    value: "badminton",
    label: "Badminton",
    appName: "Prestige Hillside Gateway Badminton",
    shortName: "PHG Badminton",
    target: 21,
    formats: [
      { bestOf: 3, target: 21 },
      { bestOf: 1, target: 21 },
      { bestOf: 1, target: 30 },
      { bestOf: 1, target: 15 },
    ],
  },
  {
    value: "pickleball",
    label: "Pickleball",
    appName: "Prestige Hillside Gateway Pickleball",
    shortName: "PHG Pickleball",
    target: 11,
    formats: [
      { bestOf: 3, target: 11 },
      { bestOf: 1, target: 11 },
      { bestOf: 1, target: 15 },
      { bestOf: 1, target: 21 },
    ],
  },
  {
    value: "tabletennis",
    label: "Table tennis",
    appName: "Prestige Hillside Gateway Table Tennis",
    shortName: "PHG Table Tennis",
    target: 11,
    formats: [
      { bestOf: 5, target: 11 },
      { bestOf: 3, target: 11 },
      { bestOf: 1, target: 11 },
      { bestOf: 1, target: 21 },
    ],
  },
];

/**
 * How an event's draw is built. `matchesPerPlayer` only means anything for `team_tie` —
 * see docs/formats/team-tie.md — and is null for everything else.
 */
export type EventFormat =
  | "round_robin"
  | "knockout"
  | "group_knockout"
  | "americano"
  | "mexicano"
  | "team_tie"
  /** Singles only, drawn a round at a time from a wins table. See packages/draw/src/swiss.ts. */
  | "swiss"
  /** Every event in an auction tournament, and nothing else. Never drawn: its teams come
   * from the auction. See 20261001000400. */
  | "auction_pool";

export const EVENT_FORMAT_LABEL: Record<EventFormat, string> = {
  round_robin: "Round robin",
  knockout: "Knockout",
  group_knockout: "Groups + knockout",
  americano: "Americano",
  mexicano: "Mexicano",
  team_tie: "Team tie",
  swiss: "Swiss",
  auction_pool: "Auction pool",
};

/**
 * Who an event is for. Open by default, and every event from before categories existed is
 * open. See 20260930000100 and, for the senior ones, 20261001000100.
 */
export type EventCategory =
  | "men"
  | "women"
  | "senior_men"
  | "senior_women"
  | "boys"
  | "girls"
  | "mixed"
  | "open";

/** In the enum's order, which is the order the event form lists them in. */
export const EVENT_CATEGORY_LABEL: Record<EventCategory, string> = {
  men: "Men",
  women: "Women",
  senior_men: "Senior Men",
  senior_women: "Senior Women",
  boys: "Boys",
  girls: "Girls",
  mixed: "Mixed",
  open: "Open",
};

/** Boys' and girls' events: juniors only, and not counting towards ratings by default. */
export const isKidsCategory = (category: EventCategory): boolean =>
  category === "boys" || category === "girls";

/** How somebody plays: the two flags read as the one answer an admin or a joiner gives. */
export type PlayingAs = "junior" | "adult" | "senior";

export const playingAs = (member: Pick<Member, "junior" | "senior">): PlayingAs =>
  member.junior ? "junior" : member.senior ? "senior" : "adult";

/**
 * The write direction: the one answer somebody gave, as the two flags stored. Both are set
 * together — adult is neither — because `members_junior_or_senior` refuses a row that is both, so
 * a junior moved to senior has to stop being a junior in the same update. `playingAs` is the
 * inverse; the two are held to each other in the tests.
 */
export const playingFlags = (as: PlayingAs): Pick<Member, "junior" | "senior"> => ({
  junior: as === "junior",
  senior: as === "senior",
});

/** Senior men's and senior women's events: seniors only. */
export const isSeniorCategory = (category: EventCategory): boolean =>
  category === "senior_men" || category === "senior_women";

/** The member category a gendered event is for, or null for mixed and open. */
export function eventGender(category: EventCategory): MemberCategory | null {
  if (category === "men" || category === "senior_men" || category === "boys") return "men";
  if (category === "women" || category === "senior_women" || category === "girls") return "women";
  return null;
}

/**
 * Why this member may not enter an event of this category, or null if they may.
 *
 * Mirrors `entry_refusal()` in SQL, which is the authority — the same arrangement as
 * `matchRates` and `waitlistPosition`. It exists so the register screen offers only the
 * people who can enter, and so the organiser's add box can say an addition was an override;
 * the database refuses regardless of what a screen offered. Same order of checks, same words.
 */
export function entryRefusal(
  category: EventCategory,
  member: Pick<Member, "category" | "junior" | "senior">,
): string | null {
  if (category === "open" || category === "mixed") return null;

  const label = {
    men: "men's",
    women: "women's",
    senior_men: "senior men's",
    senior_women: "senior women's",
    boys: "boys'",
    girls: "girls'",
  }[category];
  if (isKidsCategory(category) && !member.junior) return `${label} events are for juniors`;
  if (isSeniorCategory(category) && !member.senior) return `${label} events are for seniors`;
  if (!isKidsCategory(category) && member.junior) return `${label} events are for adults`;
  if (member.category === null) return `a category has to be set to enter a ${label} event`;
  if (member.category !== eventGender(category)) {
    return `this event is for ${category.replace("_", " ")}`;
  }
  return null;
}

/**
 * Whether a refusal is one the member can clear by changing how they play on their profile,
 * rather than by setting a category or asking an admin — a non-senior refused from a senior
 * event, or a junior refused from an adult one. The register screen links `/me` for these
 * (20261001000700); the category case has its own link and the gender case has none.
 */
export const refusalIsPlayingAs = (refusal: string): boolean =>
  refusal.endsWith("events are for seniors") || refusal.endsWith("events are for adults");

/** Whether the draw stays fixed once seeded (`rest`) or the final ordering waits on
 * earlier results (`seed_finale`, e.g. group play deciding a knockout seed). */
export type EventOrdering = "rest" | "seed_finale";

/**
 * How a doubles event turns solo entrants into pairs — requirements §5.4.
 *
 * Unread for singles, and for a team tie, which builds its own mirrored pairs per fixture.
 */
export type PairingStrategy = "snake" | "balanced" | "random";

export const PAIRING_LABEL: Record<PairingStrategy, string> = {
  snake: "Snake draft",
  balanced: "Balanced",
  random: "Random",
};

export const PAIRING_HINT: Record<PairingStrategy, string> = {
  snake: "Top seed with bottom seed, and so on down. Even teams, and easy to explain.",
  balanced: "Snake, then swapped around to even the teams up further.",
  random: "Partners drawn out of a hat. For social play.",
};

/** The player, or players, who did best in one event of a finished tournament. */
export interface EventMvp {
  eventId: string;
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  wins: number;
  pointDiff: number;
  matchesPlayed: number;
}

/** One line on a profile: MVP of an event, in a tournament. */
export interface Achievement {
  eventId: string;
  eventName: string;
  tournamentId: string;
  tournamentName: string;
  tournamentStartsOn: string;
}

export interface Tournament {
  id: string;
  name: string;
  /** Date only — a tournament is days, not instants. */
  startsOn: string;
  endsOn: string | null;
  venue: string | null;
  description: string | null;
  createdBy: string | null;
  createdAt: string;
  /** Entering any of its events needs a flat number and a phone number on file — the
   * guardian's, for a child. The database checks it; see 20261001000300. */
  contactRequired: boolean;
  /** Teams are picked by auction, so every event in it is an `auction_pool`. Chosen when the
   * tournament is made and never changed; the database holds it. See 20261001000400. */
  auction: boolean;
  /** Visible only to its invite list, their guardians, its creator and organisers. Chosen when
   * the tournament is made and never changed; the database holds it. See 20261002000300. */
  private: boolean;
  /** One image for the tournament, shared by every sport's app; null when it has none.
   * See 20261001000700. */
  logoUrl: string | null;
  /** When the auction began, or null if it has not. From then on its franchises, captains and
   * purses are fixed. See 20261001001000. */
  auctionStartedAt: string | null;
  /** The label beside this tournament's auction prices; display only. Fixed once the auction
   * has started. Held for every tournament, shown only for an auction. See 20261012000400. */
  auctionCurrency: AuctionCurrency;
  /** When an organiser finished it: scores final, MVPs named, no more results. Null until then,
   * and never worked out from dates. A finished tournament is not in the Events list. See
   * 20261002000400. */
  finishedAt: string | null;
  /** This deployment's sport only. A tournament spanning sports has events this app
   * never fetched — see lib/tournaments.ts. */
  events: TournamentEvent[];
}

/**
 * A member's flat number and phone number. Both optional, and seen only by them, organisers
 * and admins — `member_contacts` is its own table so that RLS can say so (20261001000300).
 */
export interface ContactDetails {
  flatNo: string | null;
  phone: string | null;
}

/**
 * How an organiser reaches one entrant: their own details, or — for a child, who holds none —
 * each guardian's, by name. Empty when there is nobody's to show.
 */
export interface EntryContacts {
  contacts: (ContactDetails & { guardianName: string | null })[];
  /** The tournament asks for details and this entrant lacks them — for a child, every
   * guardian does. Never true where the tournament does not ask. */
  missing: boolean;
}

/** How a team tie pairs its sides: identical seed pairs, or any close-strength pair. */
export type TiePairing = "mirrored" | "open";

export interface TournamentEvent {
  id: string;
  tournamentId: string;
  name: string;
  sport: Sport;
  kind: Kind;
  format: EventFormat;
  /** Who may enter. Fixed once anybody has registered. */
  category: EventCategory;
  matchesPerPlayer: number | null;
  /** Team tie only: Mirrored or Open, as the last draw was made. See docs/formats/team-tie.md §8. */
  tiePairing: TiePairing;
  ordering: EventOrdering;
  courts: number;
  /** A rating-bounded division, e.g. 3.0–4.0. Null on either end means unbounded. */
  minRating: number | null;
  maxRating: number | null;
  /** How many get a place. Null is uncapped, and then nobody ever waits. */
  capacity: number | null;
  /**
   * When entries close. Null means never, which is what every event had before there was
   * a cutoff at all.
   *
   * Past it, registering and withdrawing are both refused — the second is the point of it.
   * A draw is made from the entry list, and somebody letting themselves out afterwards is a
   * hole in a fixture rather than a change of mind. An organiser can still add and remove.
   */
  entriesCloseAt: string | null;
  pairing: PairingStrategy;
  /**
   * Whether matches here can move ratings. Off for a kids' event, which is the whole of what
   * makes an event a kids' event. Settled once the event has a match — see 20260929000200.
   */
  countsForRating: boolean;
  /**
   * What the name, format and category cannot say: start time, what to bring. Plain text,
   * shown with its line breaks and never parsed. Editable by an organiser at any time.
   */
  description: string | null;
  /**
   * When an organiser closed seeding for this pool, which fixes the average the organisers see.
   * Null while seeding is open, which is how every pool starts. See 20261001001100.
   */
  seedingClosedAt: string | null;
  /**
   * When an organiser published this pool's lots to its captains. Null while they are
   * unpublished, which is how every pool starts. See 20261002000100.
   */
  lotsPublishedAt: string | null;
  /** In an auction tournament, whether the pool is auctioned or its players placed by the
   * organiser. Boys' and girls' pools start as placed. See 20261012000100. */
  squadFill: SquadFill;
  /** When the organiser finished this pool's auction; its teams are public from then. */
  auctionFinishedAt: string | null;
  createdAt: string;
  /** Everyone in or waiting. The withdrawn are dropped on the way in. */
  entries: EventEntry[];
}

/**
 * One side of a fixture: known players, a slot waiting on an earlier result, or a bye.
 *
 * Mirrors `Side` in @rally/draw, which is what generated it, except that `of` is the
 * fixture's **sequence** rather than its id — the database stores a foreign key, and
 * lib/draw.ts translates it back on the way out so the UI can say "winner of match 3".
 */
export type FixtureSide =
  | { kind: "players"; memberIds: string[] }
  | { kind: "winner"; of: number }
  | { kind: "bye" };

/** What a fixture ended up being, once somebody entered a score. */
export interface FixtureResult {
  matchId: string;
  status: MatchStatus;
  games: Game[];
  winner: "A" | "B";
  playedAt: string;
  /** The database's `moves_ratings`: false for mixed, and for an event that does not count. */
  movesRatings: boolean;
}

export interface DrawFixture {
  id: string;
  round: number;
  sequence: number;
  /** Round robin only: which group. */
  pool: number | null;
  /** Team tie only: side A's seed pair, and side B's too in a Mirrored tie. */
  seedPair: number[] | null;
  /** Open team tie only: side B's seed pair. Null means the same as `seedPair`. */
  seedPairB: number[] | null;
  sideA: FixtureSide;
  sideB: FixtureSide;
  /** The match that settled it, once one exists. Null means still to play. */
  matchId: string | null;
  result: FixtureResult | null;
  /**
   * Who is actually on each side, with "winner of #3" followed through to the people who
   * won it. Null while an earlier round is still undecided — the same answer `score_fixture`
   * gives, which refuses a fixture whose feeders have not been played.
   */
  playersA: string[] | null;
  playersB: string[] | null;
}

export type RegistrationStatus = "registered" | "waitlist" | "withdrawn";

export interface EventEntry {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  status: RegistrationStatus;
  requestedAt: string;
  /** The organiser's seed, or null. Drawn first in this order; see `set_event_seeds`. */
  seed: number | null;
}

/**
 * Whether the doors have shut.
 *
 * The database is the authority — `register_for_event` and `withdraw_registration` both
 * refuse past the cutoff — and this exists so the screen does not offer a button that would
 * be refused. Clock skew can only make the app briefly kinder or briefly stricter than the
 * server, and the server always has the final say.
 */
export const areEntriesClosed = (event: { entriesCloseAt: string | null }): boolean =>
  event.entriesCloseAt !== null && new Date(event.entriesCloseAt).getTime() <= Date.now();

/**
 * Who is in, longest-waiting first among those who aren't.
 *
 * Mirrors the ordering `event_registrations.waitlist_position` uses in SQL, which is the
 * authority — the same arrangement as `matchRates` above. It exists here so a player can
 * see "3rd in the queue" without a second round trip, not so the client can decide it.
 */
export const registeredEntries = (entries: EventEntry[]): EventEntry[] =>
  entries.filter((entry) => entry.status === "registered");

export const waitlistEntries = (entries: EventEntry[]): EventEntry[] =>
  entries
    .filter((entry) => entry.status === "waitlist")
    .sort((a, b) => a.requestedAt.localeCompare(b.requestedAt));

/** 1-based place in the queue, or null when this member isn't waiting. */
export function waitlistPosition(entries: EventEntry[], memberId: string): number | null {
  const index = waitlistEntries(entries).findIndex((entry) => entry.memberId === memberId);
  return index === -1 ? null : index + 1;
}

/**
 * "6 of 16 in", "6 in", or "nobody yet".
 *
 * An uncapped event does not get a denominator, because there isn't one — showing "6 of ∞"
 * or just "6" alongside capped events would imply a limit that does not exist.
 */
export function entryCount(registered: number, capacity: number | null): string {
  if (capacity != null) return `${registered} of ${capacity} in`;
  if (registered === 0) return "nobody yet";
  return `${registered} in`;
}

export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = { 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th";
  return `${n}${suffix}`;
}

/** "22 Aug 2026", "22–23 Aug 2026", or the long form across a month boundary. */
export function formatDateRange(startsOn: string, endsOn: string | null): string {
  const start = new Date(`${startsOn}T00:00:00`);
  const startLabel = start.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  if (!endsOn || endsOn === startsOn) return startLabel;

  const end = new Date(`${endsOn}T00:00:00`);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const endLabel = end.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return sameMonth ? `${start.getDate()}–${endLabel}` : `${startLabel} – ${endLabel}`;
}

/**
 * One line of who and what: "Boys · Singles · 3.0–4.0 · 6 of 16 in · 2 waiting".
 *
 * The category leads, because it is the first thing anybody deciding whether to tap needs to
 * know — and is left off when open, which says nothing.
 */
export function describeEvent(event: TournamentEvent): string {
  const parts = event.category === "open" ? [] : [EVENT_CATEGORY_LABEL[event.category]];
  parts.push(event.kind === "singles" ? "Singles" : "Doubles");
  if (event.minRating != null || event.maxRating != null) {
    parts.push(`${event.minRating ?? "–"}–${event.maxRating ?? "–"}`);
  }
  parts.push(entryCount(registeredEntries(event.entries).length, event.capacity));

  const waiting = waitlistEntries(event.entries).length;
  if (waiting > 0) parts.push(`${waiting} waiting`);

  return parts.join(" · ");
}

export const SEED_LEVELS: { value: SeedLevel; label: string; hint: string }[] = [
  { value: "new", label: "New to the sport", hint: "Learning the rules and the swing" },
  { value: "casual", label: "Casual", hint: "Play socially, rallies hold up" },
  { value: "club", label: "Club", hint: "Regular play, tactics and placement" },
  { value: "competitive", label: "Competitive", hint: "Tournaments, coached technique" },
];

/**
 * A team of an auction tournament. It spans pools: a franchise fields a squad in each.
 * Everything but the name is optional dressing; an absent logo is drawn as initials on the
 * colour. See 20261001001000.
 */
export interface Franchise {
  id: string;
  tournamentId: string;
  name: string;
  /** Up to four characters, for tight spots. */
  shortName: string | null;
  logoUrl: string | null;
  /** `#rrggbb`. */
  colour: string | null;
  tagline: string | null;
}

/** One franchise's roster in one pool. Every franchise has one for every pool. */
export interface Squad {
  id: string;
  franchiseId: string;
  eventId: string;
  captain: { id: string; displayName: string; avatarUrl: string | null } | null;
  /** Organisers only: null for anybody else, because the database never sends it. */
  purse: number | null;
}

/** A member who can be asked to rank a pool. A login is needed; registration is not. */
export interface SeedingPanelist {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
}

/** A player in somebody's list, in the place that panelist gave them. */
export interface SeededPlayer {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  /** From 1. */
  place: number;
}

/**
 * One panelist's list, as an organiser sees it: the players they ranked, in order, and the ones
 * they skipped. `submitted` is false for a draft, which counts for nothing. Whether a submitted
 * list is still up to date depends on who is registered now, so it is worked out where that is
 * known — see `isOutOfDate` in seeding-order.ts.
 */
export interface SeedingList {
  panelist: SeedingPanelist;
  submitted: boolean;
  players: SeededPlayer[];
  skipped: SeedingPanelist[];
}

/**
 * What the panel agrees on for one player: the mean place, the median that breaks a tie, and
 * the best and worst place anybody gave them, over the lists that ranked them. `ranked` is how
 * many lists did; `lists` of `panel` is how many of the appointed panelists' lists were counted
 * at all. A player no counted list ranked has no figures. `order` is the final order, tie-break
 * included, with the unranked last.
 */
export interface SeedingEntry {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  mean: number | null;
  median: number | null;
  best: number | null;
  worst: number | null;
  ranked: number;
  lists: number;
  panel: number;
  order: number;
}

/** A pool's seeding as an organiser sees it: the panel and every list, and the live average. */
export interface PoolSeeding {
  lists: SeedingList[];
  average: SeedingEntry[];
}

/** Where a player stands in a panelist's own list. */
export type SeedingState = "ranked" | "skipped" | "unplaced";

/** A panelist's own list as the ranking screen shows it. */
export interface MySeeding {
  /** The ranked in their order, then the skipped and the unplaced by name. */
  players: (SeededPlayer & { state: SeedingState })[];
  submitted: boolean;
  /** A submitted list that no longer covers the pool. It does not count until resubmitted. */
  outOfDate: boolean;
}

/** A bidding player as a lot shows them. */
export interface LotPlayer {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * A tier of players the organiser rates alike, with the least any of them can be bought for.
 * `position` is from 1 and is the order the lots will be auctioned in. `basePrice` is whole
 * points, null until set.
 */
export interface Lot {
  id: string;
  eventId: string;
  position: number;
  /** What the organiser calls it. Null means it is shown as Lot n; see `lotLabel`. */
  name: string | null;
  basePrice: number | null;
  players: LotPlayer[];
}

/** A pool's lots in order, and the bidding players who are in none of them. */
export interface PoolLots {
  lots: Lot[];
  unassigned: LotPlayer[];
}

/** From `from` points on, a bid raises the price by `step`. See 20261011000200. */
export interface AuctionSlab {
  from: number;
  step: number;
}

/** An uneven lot's leftovers: bid for (one extra per team at most), assigned, or carried on. */
export type AuctionLeftovers = "capped_bidding" | "organiser" | "next_lot";

/**
 * A player still unsold when their lot is done: placed by the organiser, or left without a squad.
 * Every pool offers unsold players again within their lot first. See 20261012000700.
 */
export type AuctionUnsold = "organiser" | "unassigned";

/** Who comes up next within a lot. */
export type AuctionNextUp = "random" | "organiser" | "seed";

/** How a player only one squad may still take is priced. */
export type AuctionForcedPrice = "base" | "average" | "lowest_and_base" | "lowest";

/** One set of auction rules: the tournament's, or a pool's own. */
export interface AuctionRules {
  /** Whether a squad must keep the base price of every player it has still to take. */
  purseReserve: boolean;
  leftovers: AuctionLeftovers;
  unsold: AuctionUnsold;
  nextUp: AuctionNextUp;
  /** The price of a player only one squad may still take. */
  forcedPrice: AuctionForcedPrice;
  /** What the auction board shows about the player up. */
  board: { rating: boolean; level: boolean; handSide: boolean };
  /** By starting price, the first from 0. */
  slabs: AuctionSlab[];
}

/** A tournament's rules, and the pools that have rules of their own, by event id. */
export interface AuctionRulesSetup {
  tournament: AuctionRules;
  pools: ReadonlyMap<string, AuctionRules>;
}

/** How an auction pool's squads are filled. */
export type SquadFill = "auction" | "assigned";

/**
 * Where a player of an auction pool stands. `reoffer` is unsold and waiting to be offered again
 * in their lot. See `auction_players` in 20261012000700.
 */
export type AuctionPlayerState =
  | "sold"
  | "placed"
  | "up"
  | "offerable"
  | "reoffer"
  | "to_place"
  | "left_out";

/** A lot as the auction sees it: with leftovers carried in, and each franchise's share. */
export interface AuctionLot {
  lotId: string;
  position: number;
  name: string | null;
  basePrice: number | null;
  size: number;
  share: number;
  leftovers: number;
  /** Closed by an organiser with Next lot. */
  closed: boolean;
  /** Its players not yet offered, and those unsold and waiting to be offered again. */
  toOffer: number;
  toOfferAgain: number;
}

/** A squad's money and what it may do about the player up. Organisers only. */
export interface AuctionSquad {
  squadId: string;
  franchiseId: string;
  captainId: string | null;
  purse: number;
  left: number;
  /** Players taken from each lot, by lot id. */
  held: Record<string, number>;
  /** Why it cannot make the next bid, or null when it can. Null too when nobody is up. */
  refusal: string | null;
}

export interface AuctionPlayer {
  memberId: string;
  lotId: string | null;
  state: AuctionPlayerState;
  /** How many times they were marked unsold in their lot. */
  offers: number;
  squadId: string | null;
  price: number | null;
}

/** The whole of one pool's auction, as the console and the board read it. Organisers only. */
export interface AuctionPoolState {
  eventId: string;
  fill: SquadFill;
  started: boolean;
  finished: boolean;
  open: boolean;
  /** The lot the room is on, or null when nobody is left to offer. */
  currentLotId: string | null;
  up: {
    memberId: string;
    lotId: string;
    bid: number | null;
    holderId: string | null;
    nextBid: number;
    /** How many times the player was offered from the lot before: more than 0 is offered again. */
    offers: number;
    /** The one squad that may take the player, when only one may. */
    forced: { squadId: string; name: string } | null;
    details: AuctionPlayerDetails;
  } | null;
  lots: AuctionLot[];
  squads: AuctionSquad[];
  players: AuctionPlayer[];
  lastEntry:
    | {
        memberId: string;
        kind: "sold" | "unsold" | "placed";
        squadId: string | null;
        price: number | null;
        lotId: string | null;
      }
    | { memberId: null; kind: "lot_closed"; squadId: null; price: null; lotId: string }
    | null;
}

/**
 * One pool's auction as its captain reads it, while the pool is not finished: the board, every
 * squad's money and players, and `mine`, the captain's own squad with the most it can bid and
 * why it cannot raise. No other squad's max bid or refusal is ever sent. See 20261012000600.
 */
export interface AuctionCaptainState {
  eventId: string;
  open: boolean;
  currentLotId: string | null;
  up: AuctionPoolState["up"];
  lots: AuctionLot[];
  squads: Omit<AuctionSquad, "refusal">[];
  players: AuctionPlayer[];
  mine: {
    squadId: string;
    /** Purse left less the reserve, for the player up. Null when nobody is up. */
    maxBid: number | null;
    /** "holds the bid", the database's refusal for the next bid, or null when it can raise. */
    refusal: string | null;
  };
}

/** A player in a finished pool's team, as everybody may read it. No prices. */
export interface RosterPlayer {
  eventId: string;
  squadId: string;
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * What the board may say about the player up. A key is present only when the pool's rules
 * switch it on; `rating` is null for a player with no rating in the pool's discipline.
 */
export interface AuctionPlayerDetails {
  rating?: { r: number; rd: number; matches: number } | null;
  level?: SeedLevel | null;
  hand?: "left" | "right" | null;
  side?: "left" | "right" | null;
}
